/**
 * Natolie — piezas propias del carrito lateral.
 *
 *   <nt-cart-add>      tarjeta sugerida: selector de talla + botón "+ Añadir"
 *   <nt-cart-toggle>   interruptor de "Protección de envío"
 *   <nt-cart-carrusel> flechas del carrusel de sugeridos
 *
 * Todas pasan por el mismo camino que el resto del tema: lanzan
 * CartLinesUpdateEvent con una promesa y es `cart-items-component` quien
 * repinta la sección con el HTML que Shopify devuelve en `sections`. Por eso
 * la barra de descuento, el total y el contador de la cabecera se actualizan
 * solos sin tener que tocarlos aquí.
 */

import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Ids de las secciones de carrito que hay en la página, para pedirle a Shopify
 * que devuelva su HTML ya montado en la misma respuesta.
 * @returns {string[]}
 */
function idsDeSecciones() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((elemento) => (elemento instanceof HTMLElement ? elemento.dataset.sectionId : null))
    .filter(/** @returns {id is string} */ (id) => Boolean(id));
}

/**
 * Manda un cambio al carrito y deja que el tema repinte lo que haga falta.
 *
 * @param {Element} origen - Quien dispara el evento (tiene que estar en el DOM para que burbujee).
 * @param {'add'|'remove'} accion
 * @param {string} url - Theme.routes.cart_add_url o Theme.routes.cart_change_url.
 * @param {Record<string, unknown>} cuerpo - Lo que espera esa ruta de Shopify.
 * @param {Array<{merchandiseId: string, quantity: number}>} lineas - Para el evento estándar.
 */
async function cambiarCarrito(origen, accion, url, cuerpo, lineas) {
  const pendiente = CartLinesUpdateEvent.createPromise();

  origen.dispatchEvent(
    new CartLinesUpdateEvent({
      action: accion,
      context: 'cart',
      lines: lineas,
      promise: pendiente.promise,
    })
  );

  try {
    const configuracion = fetchConfig('javascript', {
      body: JSON.stringify({ ...cuerpo, sections: idsDeSecciones().join(',') }),
    });

    const respuesta = await fetch(url, {
      ...configuracion,
      headers: {
        ...configuracion.headers,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    const datos = await respuesta.json();

    if (!respuesta.ok || datos.status) {
      throw new Error(datos.description || datos.message || 'No se pudo actualizar el carrito');
    }

    // /cart/change devuelve el carrito entero; /cart/add solo las líneas añadidas.
    const carrito =
      datos.item_count === undefined
        ? await fetch(`${Theme.routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }).then((r) => r.json())
        : datos;

    pendiente.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
      detail: {
        items: carrito.items,
        sections: datos.sections,
        source: 'nt-cart',
        didError: false,
      },
    });

    return carrito;
  } catch (error) {
    pendiente.reject(error);
    origen.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Error al actualizar el carrito',
        code: 'INVALID',
      })
    );
    throw error;
  }
}

/**
 * Tarjeta de producto sugerido: cambia el precio al cambiar de talla y
 * añade la variante elegida al carrito.
 */
class NtCartAdd extends HTMLElement {
  connectedCallback() {
    this.boton = this.querySelector('.nt-reco__boton');
    this.selector = this.querySelector('.nt-reco__variantes');

    this.boton?.addEventListener('click', this.alAnadir);
    this.selector?.addEventListener('change', this.alCambiarVariante);
  }

  disconnectedCallback() {
    this.boton?.removeEventListener('click', this.alAnadir);
    this.selector?.removeEventListener('change', this.alCambiarVariante);
  }

  alCambiarVariante = () => {
    const opcion = this.selector?.selectedOptions?.[0];
    if (!opcion) return;

    const actual = this.querySelector('.nt-reco__precio-actual');
    const antes = this.querySelector('.nt-reco__precio-antes');

    if (actual && opcion.dataset.precio) actual.innerHTML = opcion.dataset.precio;

    if (antes) {
      const anterior = opcion.dataset.antes || '';
      antes.innerHTML = anterior;
      antes.hidden = anterior === '';
    }
  };

  alAnadir = async () => {
    const id = Number(this.selector?.value);
    if (!id || this.classList.contains('is-cargando')) return;

    this.classList.add('is-cargando');
    if (this.boton) this.boton.disabled = true;

    try {
      await cambiarCarrito(
        this,
        'add',
        Theme.routes.cart_add_url,
        { items: [{ id, quantity: 1 }] },
        [{ merchandiseId: String(id), quantity: 1 }]
      );
      // Si va bien, el carrito se repinta entero y esta tarjeta desaparece
      // (el producto pasa a estar en el carrito, ya no se sugiere).
    } catch (error) {
      this.classList.remove('is-cargando');
      if (this.boton) this.boton.disabled = false;
    }
  };
}

/** Interruptor de "Protección de envío": añade o quita ese producto del carrito. */
class NtCartToggle extends HTMLElement {
  connectedCallback() {
    this.boton = this.querySelector('.nt-proteccion__interruptor');
    this.boton?.addEventListener('click', this.alPulsar);
  }

  disconnectedCallback() {
    this.boton?.removeEventListener('click', this.alPulsar);
  }

  alPulsar = async () => {
    if (this.hasAttribute('data-ocupado')) return;

    const estaba = this.hasAttribute('data-activo');
    const variante = this.dataset.variante;
    const clave = this.dataset.clave;
    if (!variante) return;

    this.setAttribute('data-ocupado', '');
    // Respuesta inmediata al dedo; si falla la petición se deshace.
    this.toggleAttribute('data-activo', !estaba);
    this.boton?.setAttribute('aria-checked', String(!estaba));

    try {
      if (estaba && clave) {
        await cambiarCarrito(
          this,
          'remove',
          Theme.routes.cart_change_url,
          { id: clave, quantity: 0 },
          [{ merchandiseId: String(variante), quantity: 0 }]
        );
      } else {
        await cambiarCarrito(
          this,
          'add',
          Theme.routes.cart_add_url,
          { items: [{ id: Number(variante), quantity: 1 }] },
          [{ merchandiseId: String(variante), quantity: 1 }]
        );
      }
    } catch (error) {
      this.toggleAttribute('data-activo', estaba);
      this.boton?.setAttribute('aria-checked', String(estaba));
    } finally {
      this.removeAttribute('data-ocupado');
    }
  };
}

/** Flechas del carrusel de sugeridos. Sirven en horizontal y en vertical. */
class NtCartCarrusel extends HTMLElement {
  connectedCallback() {
    this.pista = this.querySelector('.nt-reco__pista');
    this.anterior = this.querySelector('[data-nt-flecha="anterior"]');
    this.siguiente = this.querySelector('[data-nt-flecha="siguiente"]');

    this.anterior?.addEventListener('click', this.alAnterior);
    this.siguiente?.addEventListener('click', this.alSiguiente);
    this.pista?.addEventListener('scroll', this.repasarFlechas, { passive: true });

    this.repasarFlechas();
  }

  disconnectedCallback() {
    this.anterior?.removeEventListener('click', this.alAnterior);
    this.siguiente?.removeEventListener('click', this.alSiguiente);
    this.pista?.removeEventListener('scroll', this.repasarFlechas);
  }

  get esHorizontal() {
    return Boolean(this.pista && this.pista.scrollWidth > this.pista.clientWidth + 1);
  }

  alAnterior = () => this.desplazar(-1);
  alSiguiente = () => this.desplazar(1);

  /** @param {number} sentido -1 atrás, 1 adelante */
  desplazar(sentido) {
    if (!this.pista) return;

    if (this.esHorizontal) {
      this.pista.scrollBy({ left: this.pista.clientWidth * sentido, behavior: 'smooth' });
    } else {
      this.pista.scrollBy({ top: this.pista.clientHeight * sentido, behavior: 'smooth' });
    }
  }

  repasarFlechas = () => {
    const pista = this.pista;
    if (!pista || !this.anterior || !this.siguiente) return;

    const horizontal = this.esHorizontal;
    const posicion = horizontal ? Math.abs(pista.scrollLeft) : pista.scrollTop;
    const total = horizontal ? pista.scrollWidth : pista.scrollHeight;
    const visible = horizontal ? pista.clientWidth : pista.clientHeight;
    const haySitio = total > visible + 1;

    this.anterior.disabled = !haySitio || posicion <= 1;
    this.siguiente.disabled = !haySitio || posicion >= total - visible - 1;
  };
}

if (!customElements.get('nt-cart-add')) customElements.define('nt-cart-add', NtCartAdd);
if (!customElements.get('nt-cart-toggle')) customElements.define('nt-cart-toggle', NtCartToggle);
if (!customElements.get('nt-cart-carrusel')) customElements.define('nt-cart-carrusel', NtCartCarrusel);
